//================= [JS] ======================================
import {UserNotificationApi} from '@/common/api/notification/UserNotificationApi';
import {loadNotifications} from '@/composables/layout/baseLayout';
import {Events} from '@/common/utils/Events';
import {useEventBus} from '@simpleforyou/client-core';
import {useI18n} from 'vue-i18n';
import {useStore} from '@/common/store/store';
import {fetchEventSource} from '@microsoft/fetch-event-source';

// A single shared connection across the app.
let controller: AbortController | null = null;

export function useNotificationStream() {
  const eventBus = useEventBus();
  const {t} = useI18n();
  const store = useStore();

  // OIDC — der SSE-Stream wird via @microsoft/fetch-event-source geoeffnet (nicht der
  // native EventSource, der keinen Authorization-Header setzen kann). Das OIDC-Access-
  // Token liegt im Store und wird als Bearer mitgesendet; der `fetch`-Wrapper liest es
  // bei jedem (Re-)Connect frisch, damit ein zwischenzeitlich erneuertes Token greift.
  const connect = (): void => {
    if (controller) {
      return;
    }
    controller = new AbortController();
    fetchEventSource(UserNotificationApi.stream(), {
      signal: controller.signal,
      // Beim Tab-Wechsel offen halten (Default bricht bei hidden ab).
      openWhenHidden: true,
      fetch: (input, init) => fetch(input, {
        ...init,
        credentials: 'include',
        headers: {...(init?.headers ?? {}), Authorization: `Bearer ${store.accessToken ?? ''}`},
      }),
      onmessage(event) {
        let payload: {type?: string; text?: string} = {};
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        if (payload.type !== 'notification') {
          return; // ignore heartbeats
        }
        // Refresh badge/list (cache=false in loadNotifications) and toast.
        loadNotifications();
        eventBus.emit(Events.NOTIFY, payload.text || t('navigation.notification.new'));
      },
      // Fehler NICHT werfen → fetch-event-source reconnected automatisch (mit frischem
      // Token via fetch-Wrapper). Beim Abbruch (disconnect) rejectet der Promise.
      onerror() {
        // swallow → retry
      },
    }).catch(() => {
      // aborted (disconnect) oder finaler Abbruch — Verbindung ist zu.
      controller = null;
    });
  };

  const disconnect = (): void => {
    if (controller) {
      controller.abort();
      controller = null;
    }
  };

  return {connect, disconnect};
}
